/**
 * 100ViewsOnly — Referral Service
 * Link generation and read-only stats. Awards are Cloud Functions only.
 */

import {
  doc, getDoc, setDoc, updateDoc,
  collection, getDocs, query,
  where, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";

/** Both sides, after the new user's first drop unlock. Matches Settings + handoff. */
export const SIGNUP_REFERRAL_CREDITS = 50;

/**
 * Get or create a referral code for a user
 * Code is stored on the user doc as `referralCode`
 */
export async function getReferralCode(userId) {
  if (!userId) return null;
  const userRef = doc(db, "users", userId);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return null;

  const data = snap.data();
  if (data.referralCode) return data.referralCode;

  const code = (userId.slice(0, 6) + Math.random().toString(36).slice(2, 6)).toUpperCase();

  await updateDoc(userRef, { referralCode: code });

  await setDoc(doc(db, "referralCodes", code), {
    userId,
    createdAt: serverTimestamp()
  });

  return code;
}

export function getReferralLink(code) {
  if (!code) return `${window.location.origin}/welcome?auth=signup`;
  return `${window.location.origin}/r/${encodeURIComponent(code)}`;
}

/** Creator-to-creator join link. Same code, different landing. */
export function getCreatorJoinLink(code) {
  if (!code) return `${window.location.origin}/creators/join`;
  return `${window.location.origin}/creators/join?ref=${encodeURIComponent(code)}`;
}

export function getReferralDisplayHost() {
  if (typeof window === "undefined") return "100viewsonly.com";
  return window.location.host.replace(/^www\./, "") || "100viewsonly.com";
}

export async function resolveReferralCode(code) {
  if (!code) return null;
  try {
    const snap = await getDoc(doc(db, "referralCodes", code.toUpperCase()));
    if (!snap.exists()) return null;
    return snap.data().userId;
  } catch { return null; }
}

export async function getUserReferrals(userId) {
  if (!userId) return [];
  try {
    const snap = await getDocs(query(
      collection(db, "referrals"),
      where("referrerId", "==", userId)
    ));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  } catch { return []; }
}

export async function getReferralStats(userId) {
  const referrals = await getUserReferrals(userId);
  const credited = referrals.filter((r) => r.creditsAwarded === true);
  return {
    total: referrals.length,
    unlocked: credited.length,
    creditsEarned: credited.length * SIGNUP_REFERRAL_CREDITS,
    referrals
  };
}
